<template>
  <div class="p-6 max-w-7xl mx-auto space-y-6">

    <!-- Topbar -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-800 dark:text-slate-100">Catálogo de Productos y Servicios</h1>
        <p class="text-sm text-slate-500">Administra los productos y/o servicios que tus Agentes ofrecerán por WhatsApp.</p>
      </div>
      <button @click="openModal()" class="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20">
        <Plus class="w-4 h-4" /> Nuevo Producto
      </button>
    </div>

    <!-- Filtro por Agente -->
    <div class="flex items-center gap-3 bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-800 w-fit">
      <Bot class="w-5 h-5 text-indigo-500" />
      <span class="text-xs font-bold text-slate-500 uppercase">Filtrar por Agente:</span>
      <select v-model="selectedAgentFilter" @change="loadProducts" class="bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 focus:outline-none">
        <option :value="''">Todos los agentes</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
      </select>
    </div>

    <!-- Tabla de Productos -->
    <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
      <div v-if="isLoading" class="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
        <Loader2 class="w-8 h-8 animate-spin text-indigo-500" />
        <span class="text-sm">Cargando catálogo...</span>
      </div>

      <div v-else-if="products.length === 0" class="p-12 text-center text-slate-400">
        <Package class="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p class="text-base font-semibold">No hay productos registrados</p>
      </div>

      <table v-else class="w-full text-left border-collapse">
        <thead>
          <tr class="border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            <th class="p-4">Artículo</th>
            <th class="p-4">Tipo</th>
            <th class="p-4">Precio</th>
            <th class="p-4">Stock</th>
            <th class="p-4">Estado</th>
            <th class="p-4 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
          <tr v-for="p in products" :key="p.id" class="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
            <td class="p-4">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden shrink-0 flex items-center justify-center">
                  <img v-if="p.photo_url" :src="p.photo_url" class="w-full h-full object-cover" />
                  <Package v-else class="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <div class="font-bold text-slate-800 dark:text-slate-100">{{ p.name }}</div>
                  <div class="text-xs text-slate-400 line-clamp-1">{{ p.description || 'Sin descripción' }}</div>
                </div>
              </div>
            </td>
            <td class="p-4">
              <span :class="p.type === 'Product' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'" class="px-2.5 py-1 rounded-full text-xs font-semibold">
                {{ p.type === 'Product' ? 'Producto' : 'Servicio' }}
              </span>
            </td>
            <td class="p-4 font-bold text-slate-700 dark:text-slate-200">
              $ {{ Number(p.price).toFixed(2) }}
            </td>
            <td class="p-4 text-slate-500">
              {{ p.type === 'Product' ? (p.stock !== null ? p.stock : '∞') : 'N/A' }}
            </td>
            <td class="p-4">
              <span :class="p.is_available ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'" class="px-2.5 py-1 rounded-full text-xs font-semibold">
                {{ p.is_available ? 'Activo' : 'Pausado' }}
              </span>
            </td>
            <td class="p-4 text-right space-x-2">
              <button @click="openModal(p)" class="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg">
                <Pencil class="w-4 h-4" />
              </button>
              <button @click="handleDelete(p)" class="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg">
                <Trash2 class="w-4 h-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal Formulario -->
    <ProductModal
      :show="showModal"
      :productToEdit="selectedProduct"
      :agents="agents"
      @close="showModal = false"
      @saved="loadProducts"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Plus, Bot, Package, Loader2, Pencil, Trash2 } from 'lucide-vue-next';
import ProductModal from './Form.vue';
import { useUpload } from '@/composables/useUpload';
import { useToast } from '@/composables/useToast';
import {SecureStorage} from '@/utils/secureStorage.js'; // Asegura la importación de tu helper de storage
import apiClient from '@/api/client';

const { deleteFile } = useUpload();
const { showSuccess, showError } = useToast();

const products = ref([]);
const agents = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const selectedProduct = ref(null);
const selectedAgentFilter = ref('');

// Helper para obtener parámetros de autorización según rol
const getAuthParams = () => {
  const currentUser = SecureStorage.getItem('neurals_user');
  const params = {};
  if (currentUser && currentUser.role_id === 3) {
    params.owner = currentUser.id;
  }
  return params;
};

const loadAgents = async () => {
  try {
    const params = getAuthParams();
    const res = await apiClient.get('/ai_characters', { params });
    agents.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error(e);
  }
};

const loadProducts = async () => {
  isLoading.value = true;
  try {
    const params = getAuthParams();

    // Si el usuario seleccionó un agente en el dropdown, lo agregamos al query
    if (selectedAgentFilter.value) {
      params.ai_character = selectedAgentFilter.value;
    }

    const res = await apiClient.get('/products_services', { params });
    products.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error al cargar catálogo');
  } finally {
    isLoading.value = false;
  }
};

const openModal = (product = null) => {
  selectedProduct.value = product;
  showModal.value = true;
};

const handleDelete = async (product) => {
  if (!confirm(`¿Eliminar "${product.name}"?`)) return;

  try {
    await apiClient.delete(`/products_services/${product.id}`);
    if (product.photo_url) {
      await deleteFile(product.photo_url);
    }
    showSuccess('Producto y archivo eliminados correctamente');
    loadProducts();
  } catch (e) {
    showError('Error al eliminar el producto');
  }
};

onMounted(async () => {
  await Promise.all([
    loadAgents(),
    loadProducts()
  ]);
});
</script>